import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

export default function ChoosePlan() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const clinicId = searchParams.get('clinicId');

  const [plan, setPlan] = useState('yearly');
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleApplyCoupon = () => {
    setCouponError('');
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    setCouponApplied(true);
  };

  const handleContinue = async () => {
    if (!clinicId) {
      setError('Missing clinic ID. Please go back and try again.');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const session = await api.billing.createCheckoutSession(
        clinicId,
        plan,
        couponApplied ? couponCode : ''
      );
      if (session.url) {
        window.location.href = session.url;
      } else {
        setError('Failed to create checkout session.');
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-[500px]">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/cscore-logo.png"
            alt="CScore"
            className="h-10 mx-auto"
            style={{ filter: 'brightness(0) saturate(100%) invert(13%) sepia(90%) saturate(5500%) hue-rotate(270deg) brightness(80%) contrast(110%)' }}
          />
        </div>

        <div className="bg-white rounded-xl border border-[#E6E6E6] p-8">
          <h1 className="text-[28px] font-bold text-text text-center mb-1">Choose A Plan</h1>
          <p className="text-sm text-gray-500 text-center mb-8">Free 14 day trial</p>

          {/* Plan options */}
          <div className="space-y-3 mb-8">
            <label
              className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors ${
                plan === 'yearly'
                  ? 'border-primary bg-primary/5'
                  : 'border-[#E6E6E6] hover:border-gray-300'
              }`}
              onClick={() => setPlan('yearly')}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    plan === 'yearly' ? 'border-primary' : 'border-gray-300'
                  }`}
                >
                  {plan === 'yearly' && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </div>
                <span className="text-sm font-medium text-text">Yearly Subscription</span>
              </div>
              <span className="text-sm font-semibold text-text">$999.99 / year</span>
            </label>

            <label
              className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors ${
                plan === 'monthly'
                  ? 'border-primary bg-primary/5'
                  : 'border-[#E6E6E6] hover:border-gray-300'
              }`}
              onClick={() => setPlan('monthly')}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    plan === 'monthly' ? 'border-primary' : 'border-gray-300'
                  }`}
                >
                  {plan === 'monthly' && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </div>
                <span className="text-sm font-medium text-text">Monthly Subscription</span>
              </div>
              <span className="text-sm font-semibold text-text">$99.99 / month</span>
            </label>
          </div>

          {/* Coupon code */}
          <div className="mb-8">
            <p className="text-sm font-medium text-text mb-2">Coupon Code</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value);
                  setCouponApplied(false);
                  setCouponError('');
                }}
                placeholder="Enter coupon code"
                className="flex-1 border border-[#E6E6E6] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
              />
              <button
                onClick={handleApplyCoupon}
                className="px-4 py-2.5 text-sm font-medium text-primary hover:text-primary-80 transition-colors"
              >
                Apply Code
              </button>
            </div>
            {couponApplied && (
              <p className="text-sm text-green-600 mt-1.5">Coupon applied!</p>
            )}
            {couponError && (
              <p className="text-sm text-destructive mt-1.5">{couponError}</p>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Continue button */}
          <button
            onClick={handleContinue}
            disabled={loading}
            className="w-full py-3 rounded-lg text-white font-semibold text-sm transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#CB9EF6' }}
            onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#B279F0')}
            onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#CB9EF6')}
          >
            {loading ? 'Redirecting...' : 'Continue to payment'}
          </button>
        </div>
      </div>
    </div>
  );
}
